/**
 * Format d'affichage des dates PAR LANGUE, choisi par le tenant (RTB-171, réglage back
 * `getBusinessSettings.dateFormats`).
 *
 * Le motif du tenant (jetons date-fns : `MM/dd/yyyy`, `MMM d, yyyy`…) ne décrit qu'une date
 * complète. Il remplace la partie DATE du format de la colonne et garde ce qui suit (l'heure) :
 *   - colonne `dd-MM-yyyy HH:mm` + motif `MMM d, yyyy` → `MMM d, yyyy HH:mm` ;
 *   - colonne `MM-yyyy` (mois seul), `HH:mm` (heure seule) → inchangée : pas une date complète,
 *     la colonne garde sa granularité.
 *
 * L'état actif est un singleton de module, posé par `BusinessThemeProvider` AVANT le rendu de ses
 * enfants : les formateurs AG Grid et les utilitaires d'export ne sont pas des composants React et
 * ne peuvent pas lire un contexte.
 */

/** Suggestions proposées à l'admin (en plus de la saisie libre). */
export const DATE_DISPLAY_PATTERNS = [
  "dd/MM/yyyy",
  "MM/dd/yyyy",
  "yyyy-MM-dd",
  "dd.MM.yyyy",
  "MMM d, yyyy",
  "d MMM yyyy",
  "MMMM d, yyyy",
  "d MMMM yyyy",
] as const

export const DATE_PATTERN_MAX_LENGTH = 40

export interface IDateFormatByLanguage {
  language: string
  pattern: string
}

// Miroir de `isValidDatePattern` (back, `account/dto/date-formats.dto.ts`) : même grammaire, pour
// refuser à la saisie ce que le serveur refuserait.
const PATTERN_TOKEN = /^(?:do|d{1,2}|M{1,4}|y{4}|y{2}|E{3,4}|[ /.,\-]|'[^']*')/

/** `true` si `pattern` est une date complète (jour, mois, année ; sans heure) en grammaire autorisée. */
export function isValidDatePattern(pattern: unknown): pattern is string {
  if (typeof pattern !== "string" || !pattern.trim() || pattern.length > DATE_PATTERN_MAX_LENGTH)
    return false
  const seen = new Set<string>()
  let rest = pattern
  while (rest.length) {
    const match = PATTERN_TOKEN.exec(rest)
    if (!match) return false
    if (!match[0].startsWith("'")) seen.add(match[0][0])
    rest = rest.slice(match[0].length)
  }
  return seen.has("d") && seen.has("M") && seen.has("y")
}

const DATE_LETTERS = new Set(["d", "M", "y"])
const TIME_LETTERS = new Set(["H", "h", "m", "s", "a", "k", "K"])

/**
 * Remplace la partie date de `columnFormat` par `pattern` ; rend `columnFormat` tel quel si ce
 * n'est pas une date complète (jour, mois, année) placée AVANT toute heure.
 */
export function localizeDateFormat(columnFormat: string, pattern?: string | null): string {
  if (!pattern || !columnFormat) return columnFormat
  const seen = new Set<string>()
  let lastDateIndex = -1
  let inQuote = false
  for (let i = 0; i < columnFormat.length; i++) {
    const char = columnFormat[i]
    if (char === "'") {
      inQuote = !inQuote
      continue
    }
    if (inQuote) continue
    if (DATE_LETTERS.has(char)) {
      if ([...seen].some(letter => TIME_LETTERS.has(letter))) return columnFormat
      seen.add(char)
      lastDateIndex = i
    } else if (TIME_LETTERS.has(char)) {
      seen.add(char)
    }
  }
  if (!(seen.has("d") && seen.has("M") && seen.has("y"))) return columnFormat
  return pattern + columnFormat.slice(lastDateIndex + 1)
}

/** Motif du tenant pour une langue (`"en"`, `"fr"`…), ou `undefined`. */
export function datePatternForLanguage(
  dateFormats: ReadonlyArray<IDateFormatByLanguage> | null | undefined,
  language: string | undefined
): string | undefined {
  if (!language) return undefined
  return dateFormats?.find(entry => entry?.language === language)?.pattern || undefined
}

let activeDatePattern: string | undefined

export function setActiveDatePattern(pattern: string | undefined): void {
  activeDatePattern = pattern || undefined
}

export function getActiveDatePattern(): string | undefined {
  return activeDatePattern
}
